import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Settings,
  ArrowRight,
  Mail,
  Calendar,
  FileText,
  Shield,
  Clock,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

const features = [
  {
    icon: Mail,
    title: "Outreach Automation",
    description: "AI-drafted personalized outreach to funders, prioritized by FundMatch scores and tuned to funder priorities.",
    stats: "<30% user edit rate",
  },
  {
    icon: Calendar,
    title: "Follow-Up Manager",
    description: "Track all pending applications, get smart reminders, and never miss a deadline with timeline-aware follow-ups.",
    stats: "100% on-time rate",
  },
  {
    icon: FileText,
    title: "AI-Assisted Reports",
    description: "Generate funder reports in under 30 minutes with AI-structured, data-driven narrative drafts.",
    stats: ">70% first-draft acceptance",
  },
  {
    icon: Shield,
    title: "Compliance Manager",
    description: "Automated condition extraction, deliverable tracking, and pre-breach alerts for 100% compliance.",
    stats: "0% breach rate",
  },
];

const workflowSteps = [
  { step: 1, title: "AI extracts org goals", description: "From L1-L4 profile" },
  { step: 2, title: "User confirms goal", description: "One-click confirmation" },
  { step: 3, title: "AI generates draft", description: "Personalized to funder" },
];

export const AdminHubPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 gradient-hero" data-testid="adminhub-hero">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Badge className="mb-6 bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 px-4 py-1.5 text-sm font-medium">
                  <Settings className="h-3.5 w-3.5 mr-1.5" />
                  Administration
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-zinc-900 tracking-tight mb-6"
              >
                AdminHub
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg md:text-xl text-zinc-600 leading-relaxed mb-8"
              >
                Eliminate administrative overhead and focus on your mission. Automate the complete 
                lifecycle of funder interactions from initial outreach through compliance.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    className="bg-amber-500 hover:bg-amber-600 text-white font-medium px-8"
                    data-testid="adminhub-cta"
                  >
                    Start Automating
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="grid grid-cols-3 gap-6 mt-12"
              >
                <div>
                  <p className="text-3xl font-heading font-bold text-zinc-900">10+</p>
                  <p className="text-sm text-zinc-500">Hours saved/week</p>
                </div>
                <div>
                  <p className="text-3xl font-heading font-bold text-zinc-900">100%</p>
                  <p className="text-sm text-zinc-500">On-time follow-ups</p>
                </div>
                <div>
                  <p className="text-3xl font-heading font-bold text-zinc-900">0%</p>
                  <p className="text-sm text-zinc-500">Compliance breaches</p>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-zinc-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold text-zinc-900">Pending Actions</h3>
                  <Badge className="bg-red-100 text-red-700">7 items</Badge>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl border border-red-100">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-zinc-900">Q4 Progress Report Due</p>
                      <p className="text-xs text-zinc-500">Youth Mentorship Program</p>
                    </div>
                    <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-zinc-900">Follow up with OTF</p>
                      <p className="text-xs text-zinc-500">Community Resilience Fund</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 text-xs">High</Badge>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-zinc-900">Draft outreach ready</p>
                      <p className="text-xs text-zinc-500">Vancouver Foundation</p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">Ready</Badge>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32 bg-white" data-testid="adminhub-features">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">
              Complete Automation
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-zinc-900 tracking-tight mb-4">
              Four pillars of admin automation
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              From outreach to compliance, AdminHub handles the administrative work so you can focus on impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-zinc-50 rounded-2xl p-8 border border-zinc-100 hover:border-amber-200 transition-all"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-heading font-semibold text-zinc-900">{feature.title}</h3>
                      <Badge variant="outline" className="text-xs">{feature.stats}</Badge>
                    </div>
                    <p className="text-zinc-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 md:py-32 bg-zinc-50">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">
              Simple Workflow
            </Badge>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-zinc-900 tracking-tight mb-4">
              3-Step Outreach Automation
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {workflowSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.15 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-amber-500 text-white flex items-center justify-center mx-auto mb-4 text-2xl font-heading font-bold">
                  {step.step}
                </div>
                <h3 className="font-heading font-semibold text-zinc-900 mb-2">{step.title}</h3>
                <p className="text-sm text-zinc-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-amber-500">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight mb-6">
            Reclaim your time
          </h2>
          <p className="text-lg text-amber-100 mb-10 max-w-2xl mx-auto">
            Let AI handle the admin work. Save 10+ hours per week and never miss another deadline.
          </p>
          <Link to="/dashboard">
            <Button
              size="lg"
              className="bg-white text-amber-700 hover:bg-zinc-100 font-medium px-8 py-6 text-base"
            >
              Start Automating
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AdminHubPage;
